import React, { useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Logo } from "@/components/Brand";

const shell = "flex min-h-screen flex-col bg-[#090A0C] text-slate-50 evo-blueprint-grid";
const card = "rounded-2xl border border-slate-800 bg-[#151820] p-7 shadow-2xl";
const inp = "w-full rounded-lg border border-slate-700 bg-[#0d0f13] px-4 py-3 text-slate-100 outline-none focus:border-amber-500";
const btn = "flex w-full items-center justify-center rounded-xl bg-gradient-to-r from-amber-600 to-amber-500 px-6 py-3.5 font-bold text-slate-950 transition-all hover:from-amber-500 hover:to-amber-400 active:scale-95 disabled:opacity-60";

export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try { await api.post("/auth/forgot-password", { email }); setSent(true); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  return (
    <div className={shell}>
      <div className="mx-auto flex w-full max-w-md flex-1 flex-col justify-center px-4 py-12">
        <Link to="/login" className="mb-8 inline-flex items-center gap-2 text-sm text-slate-400 hover:text-amber-400"><ArrowLeft className="h-4 w-4" /> Back to sign in</Link>
        <div className={card}>
          <Logo />
          <h1 className="mt-6 font-heading text-2xl font-bold">Reset your password</h1>
          {sent ? (
            <p className="mt-3 text-sm text-slate-300" data-testid="forgot-success">
              If an account exists for <strong>{email}</strong>, we've sent a password reset link. Please check your inbox (and spam folder).
            </p>
          ) : (
            <>
              <p className="mt-1 text-sm text-slate-400">Enter your email and we'll send you a link to reset your password.</p>
              <form onSubmit={submit} className="mt-6 space-y-4">
                <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className={inp} placeholder="you@example.com" data-testid="forgot-email-input" />
                <button type="submit" disabled={loading} className={btn} data-testid="forgot-submit-btn">{loading ? "Sending…" : "Send reset link"}</button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const submit = async (e) => {
    e.preventDefault();
    if (password !== confirm) return toast.error("Passwords do not match");
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      toast.success("Password updated — please sign in");
      navigate("/login");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  return (
    <div className={shell}>
      <div className="mx-auto flex w-full max-w-md flex-1 flex-col justify-center px-4 py-12">
        <div className={card}>
          <Logo />
          <h1 className="mt-6 font-heading text-2xl font-bold">Choose a new password</h1>
          {!token ? (
            <p className="mt-3 text-sm text-rose-400">This reset link is invalid. Please request a new one from the sign-in page.</p>
          ) : (
            <form onSubmit={submit} className="mt-6 space-y-4">
              <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className={inp} placeholder="New password" data-testid="reset-password-input" />
              <input type="password" required value={confirm} onChange={(e) => setConfirm(e.target.value)} className={inp} placeholder="Confirm new password" data-testid="reset-confirm-input" />
              <button type="submit" disabled={loading} className={btn} data-testid="reset-submit-btn">{loading ? "Updating…" : "Update password"}</button>
            </form>
          )}
          <p className="mt-5 text-center text-sm text-slate-400"><Link to="/login" className="font-semibold text-amber-400 hover:underline">Back to sign in</Link></p>
        </div>
      </div>
    </div>
  );
}
